import {
    AlertModal,
    Box,
    Button,
    Heading,
    Modal,
    showToast,
    TabMenu,
    TabMenuItem,
    TextBlock,
    TextInput,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./integration-configuration-modal.scss";
import { IntegrationSyncLogs } from "./IntegrationSyncLogs";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { hasHttpStatus } from "domain/fileSharing";
import { ScheduleRequest } from "domain/workflows";
import { TemplateSchedule } from "domain/workflowTemplates";
import { ConflictBehavior, templateService } from "services/workflows/TemplateService";
import { workflowService } from "services/workflows/WorkflowService";
import { logger } from "utils/logging";
import { DayOfWeek, formatDaysOfWeek } from "utils/scheduleUtils";
import { formatOffset } from "utils/timezones";

import testIds from "testIds.json";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    isSaved: boolean;
    onIntegrationStarted: (workflowUuid: string) => void;
    onSuccess?: () => void;
    templateName: string;
    templateUuid: string;
    parameters?: Record<string, unknown>;
    workflowUuid?: string;
    schedule?: TemplateSchedule;
}

const formatLabel = (key: string): string => key.replace(/([A-Z])/g, " $1").trim();

const toStringParameterEntries = (parameters: Record<string, unknown>): [string, string][] =>
    Object.entries(parameters).filter((entry): entry is [string, string] => typeof entry[1] === "string");

const toScheduleRequest = (schedule: TemplateSchedule, parameters: Record<string, unknown>): ScheduleRequest => ({
    frequency: schedule.frequency,
    recurrence: schedule.recurrence,
    timezone: schedule.timezone,
    enabled: true,
    parameters,
});

interface DetailRowProps {
    label: string;
    children: React.ReactNode;
}

const DetailRow = (props: DetailRowProps) => (
    <Box gap={"Sm"} verticalAlignment={"Center"}>
        <Box className={style.detailRowLabel}>
            <span>{props.label}</span>
        </Box>
        <Box className={style.detailRowContent}>{props.children}</Box>
    </Box>
);

interface ParameterInputProps {
    name: string;
    displayValue: string;
    onChange: (value: string) => void;
}

const ParameterInput = (props: ParameterInputProps) => (
    <TextInput
        name={props.name}
        hideLabelSection
        value={props.displayValue}
        onChange={(e) => props.onChange(e.target.value)}
        testIds={{ input: testIds.workArea.asset.integrations.configurationModal.parameterInput }}
    />
);

const IntegrationConfigurationModal = (props: Props) => {
    const { t } = useTranslation();
    const [submitLoading, setSubmitLoading] = React.useState(false);
    const [parameters, setParameters] = React.useState<Record<string, unknown>>(props.parameters ?? {});
    const [schedule, setSchedule] = React.useState<TemplateSchedule | undefined>(props.schedule);
    const [scheduleLoading, setScheduleLoading] = React.useState(false);
    const [activeTabIndex, setActiveTabIndex] = React.useState(0);
    const [scheduleUpdateWorkflowUuid, setScheduleUpdateWorkflowUuid] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        setActiveTabIndex(0);

        if (props.isSaved && props.workflowUuid !== undefined) {
            if (props.parameters !== undefined && props.schedule !== undefined) {
                setParameters(props.parameters);
                setSchedule(props.schedule);
                return;
            }
            const abortController = new AbortController();
            setScheduleLoading(true);
            workflowService
                .fetchSchedule(props.workflowUuid, abortController)
                .then((response) => {
                    setParameters(response.parameters);
                    setSchedule({
                        frequency: response.frequency,
                        recurrence: response.recurrence,
                        timezone: response.timezone,
                    });
                })
                .catch((exception) => {
                    // Fetch fails when the workflow has no schedule saved yet, so fall back to the props.
                    logger.warn("Failed to fetch integration schedule", exception.message);
                    setParameters(props.parameters ?? {});
                    setSchedule(props.schedule);
                })
                .finally(() => {
                    setScheduleLoading(false);
                });
            return () => abortController.abort();
        }

        setParameters(props.parameters ?? {});
        setSchedule(props.schedule);
    }, [props.isOpen, props.isSaved, props.workflowUuid]);

    const handleTopLevelChange = (key: string, value: string) => {
        setParameters((prev) => ({ ...prev, [key]: value }));
    };

    const handleUpdateSchedule = async (workflowUuid: string) => {
        if (schedule === undefined) {
            props.onClose();
            return;
        }

        const abortController = new AbortController();
        const scheduleRequest = toScheduleRequest(schedule, parameters);

        try {
            try {
                await workflowService.updateSchedule(workflowUuid, scheduleRequest, abortController);
            } catch (exception) {
                if (!hasHttpStatus(exception) || exception.status !== 404) {
                    throw exception;
                }
                // PUT fails with 404 when the workflow has no schedule saved yet, so create one instead.
                await workflowService.createSchedule(workflowUuid, scheduleRequest, abortController);
            }

            setScheduleUpdateWorkflowUuid(undefined);
            showToast(t("IntegrationConfigurationModal.saveSuccess"), "success");
            props.onSuccess?.();
            props.onClose();
        } catch (exception) {
            logger.warn("Failed to update integration schedule", exception.message);
            setScheduleUpdateWorkflowUuid(workflowUuid);
        }
    };

    const handleRetryScheduleUpdate = () => {
        if (scheduleUpdateWorkflowUuid !== undefined) {
            handleUpdateSchedule(scheduleUpdateWorkflowUuid);
        }
    };

    const handleClose = () => {
        props.onClose();
        setScheduleUpdateWorkflowUuid(undefined);
    };

    const handleSave = () => {
        setSubmitLoading(true);
        const abortController = new AbortController();
        templateService
            .save(props.templateUuid, ConflictBehavior.OVERWRITE, undefined, abortController)
            .then((response) => {
                props.onIntegrationStarted(response.workflowUuid);
                handleUpdateSchedule(response.workflowUuid);
            })
            .finally(() => {
                setSubmitLoading(false);
            });
    };

    const handleOk = () => {
        if (props.isSaved && props.workflowUuid !== undefined) {
            handleUpdateSchedule(props.workflowUuid);
        } else {
            handleSave();
        }
    };

    const isMissingSchedule = schedule === undefined;

    return (
        <>
            <Modal
                title={props.templateName}
                isOpen={props.isOpen}
                hideModal={props.onClose}
                width="Lg"
                buttons={
                    <Tooltip placement="top">
                        <TooltipTrigger>
                            <Button
                                onClick={handleOk}
                                variant={"Primary"}
                                isLoading={submitLoading}
                                disabled={submitLoading || scheduleLoading || isMissingSchedule}
                            >
                                {t("Common.ok")}
                            </Button>
                        </TooltipTrigger>
                        {isMissingSchedule && (
                            <TooltipContent>{t("IntegrationConfigurationModal.noScheduleTooltip")}</TooltipContent>
                        )}
                    </Tooltip>
                }
            >
                <Box columnLayout gap="Lg" className={style.tabs}>
                    <TabMenu>
                        <TabMenuItem key={0} isActive={activeTabIndex === 0} onClick={() => setActiveTabIndex(0)}>
                            {t("Integrations.details.tabs.settings")}
                        </TabMenuItem>
                        <TabMenuItem key={1} isActive={activeTabIndex === 1} onClick={() => setActiveTabIndex(1)}>
                            {t("Integrations.details.tabs.syncLogs")}
                        </TabMenuItem>
                    </TabMenu>
                    {activeTabIndex === 0 && (
                        <div className={style.tabPanel}>
                            {scheduleLoading ? (
                                <LoadingIndicator />
                            ) : (
                                <Box columnLayout gap={"Sm"}>
                                    {schedule !== undefined && (
                                        <div>
                                            <Heading variant={"SUBTITLE_1"} className={style.label}>
                                                {t("IntegrationConfigurationModal.scheduleLabel")}
                                            </Heading>
                                            <Box columnLayout gap={"Xs"}>
                                                <DetailRow label={t("ScheduleWorkflow.launchSchedule")}>
                                                    <TextBlock>
                                                        {t(
                                                            `ScheduleWorkflow.launchScheduleOptions.${schedule.frequency.toLowerCase()}`
                                                        )}
                                                    </TextBlock>
                                                </DetailRow>
                                                {schedule.recurrence.days !== undefined && (
                                                    <DetailRow label={t("ScheduleWorkflow.daysOfWeek")}>
                                                        <TextBlock>
                                                            {formatDaysOfWeek(
                                                                schedule.recurrence.days as DayOfWeek[],
                                                                t
                                                            )}
                                                        </TextBlock>
                                                    </DetailRow>
                                                )}
                                                {schedule.recurrence.day !== undefined && (
                                                    <DetailRow label={t("ScheduleWorkflow.dayOfMonth")}>
                                                        <TextBlock>{schedule.recurrence.day}</TextBlock>
                                                    </DetailRow>
                                                )}
                                                {schedule.recurrence.time !== undefined && (
                                                    <DetailRow label={t("ScheduleWorkflow.launchTime")}>
                                                        <TextBlock>{schedule.recurrence.time}</TextBlock>
                                                    </DetailRow>
                                                )}
                                                {schedule.recurrence.every !== undefined && (
                                                    <DetailRow label={t("ScheduleWorkflow.every")}>
                                                        <TextBlock>
                                                            {`${schedule.recurrence.every} ${t("ScheduleWorkflow.hours")}`}
                                                        </TextBlock>
                                                    </DetailRow>
                                                )}
                                                <DetailRow label={t("ScheduleWorkflow.timezone")}>
                                                    <TextBlock>
                                                        {`${schedule.timezone} ${formatOffset(schedule.timezone)}`}
                                                    </TextBlock>
                                                </DetailRow>
                                                <DetailRow label={t("Common.status")}>
                                                    <TextBlock>{"TODO BCC-6779 WIP"}</TextBlock>
                                                </DetailRow>
                                            </Box>
                                        </div>
                                    )}
                                    <div>
                                        <Heading variant={"SUBTITLE_1"} className={style.label}>
                                            {t("IntegrationConfigurationModal.parametersLabel")}
                                        </Heading>
                                        <Box columnLayout gap={"Xs"}>
                                            {toStringParameterEntries(parameters).map(([key, value]) => (
                                                <DetailRow key={key} label={formatLabel(key)}>
                                                    <ParameterInput
                                                        name={key}
                                                        displayValue={value}
                                                        onChange={(val) => handleTopLevelChange(key, val)}
                                                    />
                                                </DetailRow>
                                            ))}
                                        </Box>
                                    </div>
                                </Box>
                            )}
                        </div>
                    )}
                    {activeTabIndex === 1 && (
                        <div className={classNames(style.tabPanel, style.syncLogsTabPanel)}>
                            {props.workflowUuid !== undefined && (
                                <IntegrationSyncLogs workflowUuid={props.workflowUuid} />
                            )}
                        </div>
                    )}
                </Box>
            </Modal>
            <AlertModal
                title={t("IntegrationConfigurationModal.scheduleUpdateError.title")}
                variant={"Error"}
                isOpen={scheduleUpdateWorkflowUuid !== undefined}
                hideModal={props.onClose}
                buttons={
                    <Box gap="Md">
                        <Button onClick={handleRetryScheduleUpdate} variant={"Primary"}>
                            {t("Common.retry")}
                        </Button>
                        <Button onClick={handleClose} variant={"Secondary"}>
                            {t("Common.cancel")}
                        </Button>
                    </Box>
                }
            >
                <TextBlock>{t("IntegrationConfigurationModal.scheduleUpdateError.message")}</TextBlock>
            </AlertModal>
        </>
    );
};

export default IntegrationConfigurationModal;
